import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import ConfidenceSelector from "../components/ConfidenceSelector";
import { StageLoading } from "../components/Loading";
import { C } from "../lib/theme";
import { ArrowRight, Check, X, AlertTriangle } from "lucide-react";

export default function Stage0({ session, onComplete }) {
  const questions = session.stage0.questions;
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [confidence, setConfidence] = useState(Array(questions.length).fill(null));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const q = questions[idx];
  const isMcq = q.kind === "mcq";
  const ans = answers[idx];
  const answered = isMcq ? ans !== null : typeof ans === "string" && ans.trim().length > 0;
  const canNext = answered && confidence[idx] !== null;
  const isLast = idx === questions.length - 1;

  const setAnswer = (v) => setAnswers((a) => { const n = [...a]; n[idx] = v; return n; });
  const setConf = (v) => setConfidence((c) => { const n = [...c]; n[idx] = v; return n; });

  const submit = useCallback(async () => {
    setSubmitting(true);
    try {
      const r = await api.submitStage0(session.id, answers, confidence);
      setResult(r);
    } finally {
      setSubmitting(false);
    }
  }, [answers, confidence, session.id]);

  const next = useCallback(() => {
    if (!canNext) return;
    if (isLast) submit(); else setIdx((i) => i + 1);
  }, [canNext, isLast, submit]);

  useEffect(() => {
    const onKey = (e) => {
      if (result) return;
      const q2 = questions[idx];
      if (q2.kind === "mcq") {
        const k = e.key.toLowerCase();
        if (["a", "b", "c", "d"].includes(k)) { setAnswer(["a", "b", "c", "d"].indexOf(k)); return; }
        if (["1", "2", "3", "4"].includes(k)) { setConf(parseInt(k)); return; }
        if (e.key === "Enter") next();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [idx, result, next, questions]);

  if (submitting && !result) {
    return <StageLoading color={C.vio} title="Scoring your baseline…" sub="Reading your written answers and factoring in your confidence." />;
  }

  if (result) {
    const cal = result.calibration || {};
    return (
      <div className="mx-auto max-w-2xl anim-fade-up" data-testid="stage0-result">
        <div className="mono-label">SYS · BASELINE</div>
        <h2 className="mt-1 font-display text-3xl tracking-tight" style={{ color: C.vio }}>What you already knew</h2>
        <div className="mt-4 rounded-[14px] border border-hair bg-surface p-5">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-sm text-muted">Confidence-weighted baseline</span>
            <span className="count-up font-mono text-3xl tab-nums" style={{ color: C.vio }}>{result.score}%</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-white/5">
            <div className="h-full origin-left rounded-full transition-all duration-700" style={{ width: `${result.score}%`, background: C.vio }} />
          </div>
          <p className="mt-2 font-mono text-[10px] text-muted">Guessing right counts less than knowing. Your score reflects real understanding.</p>
        </div>
        <div className="mt-4 space-y-2">
          {result.review.map((p, i) => (
            <div
              key={i}
              data-testid={`stage0-review-${i}`}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              style={{ borderColor: p.confidentButWrong ? C.ros : "rgba(255,255,255,0.08)", boxShadow: p.confidentButWrong ? `0 0 0 1px ${C.ros}` : "none" }}
            >
              <span className="flex items-center gap-2 text-ink/80">
                {p.correct ? <Check className="h-4 w-4 text-grn" /> : <X className="h-4 w-4 text-ros" />}
                <span className="line-clamp-1">{p.concept}</span>
                {p.guessedRight && <span className="rounded px-1.5 font-mono text-[9px]" style={{ background: `${C.amb}22`, color: C.amb }}>guessed</span>}
              </span>
              <span className="font-mono text-xs text-muted">conf {p.confidence}</span>
            </div>
          ))}
        </div>
        {cal.confidentWrong > 0 && (
          <p className="mt-3 flex items-center gap-1.5 text-xs text-ros/90"><AlertTriangle className="h-3.5 w-3.5" /> {cal.confidentWrong} answer(s) you were sure about were wrong — we'll target those.</p>
        )}
        <p className="mt-3 text-sm text-muted">We'll move fast on what you know and slow down on the rest.</p>
        <button data-testid="start-learning-btn" onClick={onComplete} className="press mt-6 flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-bg" style={{ background: C.blu }}>
          Start learning <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl" data-testid="stage0">
      <div className="mb-1 font-mono text-xs" style={{ color: C.vio }}>STAGE 0 · CHECK · {idx + 1}/{questions.length}</div>
      <h2 className="font-display text-2xl tracking-tight sm:text-3xl">What do you already know about {session.topic.replace(/\?$/, "")}?</h2>

      <div key={idx} className="mt-6 anim-fade-up">
        <div className="mb-2 mono-label">{isMcq ? "MULTIPLE CHOICE" : "WRITE YOUR ANSWER"}</div>
        <p className="text-lg text-ink">{q.prompt}</p>

        {isMcq ? (
          <div className="mt-4 space-y-2">
            {q.options.map((o, i) => {
              const active = ans === i;
              return (
                <button
                  key={i}
                  data-testid={`stage0-opt-${i}`}
                  onClick={() => setAnswer(i)}
                  className="press flex w-full items-center gap-3 rounded-md border px-3 py-3 text-left text-sm"
                  style={{ borderColor: active ? C.vio : "rgba(255,255,255,0.08)", background: active ? `${C.vio}18` : "transparent" }}
                >
                  <span className="flex h-6 w-6 items-center justify-center rounded font-mono text-xs" style={{ background: active ? C.vio : "rgba(255,255,255,0.06)", color: active ? "#0B0D12" : "#8B93A5" }}>
                    {["A", "B", "C", "D"][i]}
                  </span>
                  {o}
                </button>
              );
            })}
          </div>
        ) : (
          <textarea
            data-testid="stage0-text-input"
            value={ans || ""}
            onChange={(e) => setAnswer(e.target.value)}
            rows={4}
            placeholder="A sentence or two in your own words. No options — this is where guessing can't help."
            className="mt-4 w-full resize-none rounded-lg border border-hair bg-surface2 px-3 py-3 text-sm text-ink placeholder:text-muted focus:border-vio/60 focus:outline-none"
          />
        )}

        <ConfidenceSelector value={confidence[idx]} onChange={setConf} idPrefix="stage0-conf" />

        <div className="mt-6 flex items-center justify-between">
          <span className="font-mono text-[11px] text-muted">{isMcq ? "A–D · 1–4 · Enter" : "Answer in your own words"}</span>
          <button
            data-testid="stage0-next-btn"
            onClick={next}
            disabled={!canNext}
            className="press flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-bg disabled:opacity-40"
            style={{ background: C.vio }}
          >
            {isLast ? "Submit" : "Next"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
